import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FiColumns, FiDownload, FiRefreshCw } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { getSalesTransactionExportPage, getSalesTransactionReport } from 'services/retail-report.service';
import useVerifyAccess from 'hooks/useVerifyAccess';
import SalesReportFilters, { getSalesReportQuery } from './SalesReportFilters';
import {
  ReportContextBar,
  ReportPageHeader,
  ReportPageLayout,
  ReportState,
  ReportTableToolbar,
} from './ReportComponents';
import './retailReports.scss';

const money = (value) =>
  value === null || value === undefined
    ? '—'
    : `${new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Number(value) || 0)} đ`;

const number = (value) => new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
const dateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
};

const formatDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const salespersonDisplay = (item) => item.salesperson_display || item.salespersonname || 'Chưa xác định nhân viên bán hàng';

const allocationNumber = (value) => value === null || value === undefined ? null : Number(value);
const allocationPercent = (value) => value === null || value === undefined
  ? '—'
  : `${new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 }).format(Number(value) || 0)}%`;
const scopeIdentity = (scope = {}) => JSON.stringify([
  scope.company_id, scope.business_id, scope.store_id,
].map((value) => String(value ?? '')));

const buildExportRows = (items = [], includeAllocation = false) =>
  items.map((item, index) => ({
    STT: index + 1,
    'Mã đơn': item.orderno,
    'Ngày bán': dateTime(item.orderdate),
    'Cửa hàng': item.storename,
    'Trạng thái': item.statusname,
    'Khách hàng': item.customername || 'Khách lẻ',
    'Số điện thoại': item.phonenumber || '',
    'Mã sản phẩm': item.productcode,
    'Tên sản phẩm': item.productname,
    'Số lượng': Number(item.quantity) || 0,
    'Giá bán': Number(item.saleprice) || 0,
    'Chiết khấu dòng': Number(item.linediscount) || 0,
    'Thuế': Number(item.vatamount) || 0,
    'Thành tiền': Number(item.totalprice) || 0,
    ...(includeAllocation ? {
      'Doanh số phân bổ': allocationNumber(item.allocatedrevenue),
      'Tỷ lệ phân bổ (%)': allocationNumber(item.allocationpercent),
      'Số nhân viên bán hàng': allocationNumber(item.salespersoncount),
    } : {}),
    'Nhân viên bán hàng': salespersonDisplay(item),
    'Người tạo đơn': item.createdusername,
    'Thu ngân': item.cashiername || 'Chưa ghi nhận',
    'Phương thức thanh toán': item.paymentmethods || 'Chưa ghi nhận',
    ...(item.cogsprice !== undefined ? { 'Giá vốn': Number(item.cogsprice) || 0 } : {}),
    ...(item.grossprofit !== undefined ? { 'Lợi nhuận gộp': Number(item.grossprofit) || 0 } : {}),
  }));

const TRANSACTION_COLUMNS = [
  {
    key: 'order',
    label: 'Mã đơn',
    required: true,
    className: 'is-order',
    render: (item) => (
      <Link to={`/orders/edit/${item.orderid}?tab_active=information`}>{item.orderno || '—'}</Link>
    ),
  },
  { key: 'date', label: 'Ngày bán', className: 'is-date', render: (item) => dateTime(item.orderdate) },
  {
    key: 'product',
    label: 'Sản phẩm',
    required: true,
    className: 'is-product',
    render: (item) => (
      <div title={[item.productcode, item.productname].filter(Boolean).join(' - ')}>
        <strong>{item.productcode || '—'}</strong>
        <span>{item.productname || 'Chưa có tên sản phẩm'}</span>
      </div>
    ),
  },
  { key: 'quantity', label: 'SL', className: 'is-number is-quantity', render: (item) => number(item.quantity) },
  { key: 'amount', label: 'Thành tiền', className: 'is-number is-money', render: (item) => money(item.totalprice) },
  {
    key: 'allocatedrevenue',
    label: 'Doanh số phân bổ',
    requiresSalespersonFilter: true,
    className: 'is-number is-money',
    render: (item) => money(item.allocatedrevenue),
  },
  {
    key: 'allocationpercent',
    label: 'Tỷ lệ phân bổ',
    requiresSalespersonFilter: true,
    className: 'is-number',
    render: (item) => allocationPercent(item.allocationpercent),
  },
  {
    key: 'salesperson',
    label: 'Nhân viên bán hàng',
    className: 'is-role',
    render: salespersonDisplay,
  },
  {
    key: 'creator',
    label: 'Người tạo đơn',
    className: 'is-role',
    render: (item) => item.createdusername || 'Chưa ghi nhận',
  },
  {
    key: 'cashier',
    label: 'Thu ngân',
    className: 'is-role',
    render: (item) => item.cashiername || 'Chưa ghi nhận',
  },
  {
    key: 'payment',
    label: 'Thanh toán',
    className: 'is-payment',
    render: (item) => item.paymentmethods || 'Chưa ghi nhận',
  },
  { key: 'status', label: 'Trạng thái', className: 'is-status', render: (item) => item.statusname || '—' },
];

const SalesTransactionReportPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { verifyPermission } = useVerifyAccess();
  const canExport = verifyPermission('SL_SALES_REPORT_EXPORT');
  const query = useMemo(() => getSalesReportQuery(location.search), [location.search]);
  const [portalScope, setPortalScope] = useState(() => getPortalScope());
  const [data, setData] = useState({ summary: {}, items: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const latestRequestRef = useRef(0);
  const latestExportRef = useRef(0);
  const contextKey = JSON.stringify([location.pathname, location.search, scopeIdentity(portalScope)]);
  const currentContextRef = useRef(contextKey);
  currentContextRef.current = contextKey;
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() =>
    TRANSACTION_COLUMNS.map((column) => column.key),
  );

  useEffect(() => {
    const handleScopeChange = (event) => {
      latestExportRef.current += 1;
      setExporting(false);
      setPortalScope(event?.detail || getPortalScope());
    };
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
  }, []);

  useEffect(() => {
    latestExportRef.current += 1;
    setExporting(false);
    return () => { latestExportRef.current += 1; };
  }, [contextKey]);

  const load = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    if (!portalScope.store_id) {
      setData({ summary: {}, items: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setData({ summary: {}, items: [] });
    try {
      const response = await getSalesTransactionReport(query);
      if (requestId === latestRequestRef.current) setData(response);
    } catch (requestError) {
      if (requestId === latestRequestRef.current) {
        setError(requestError?.message || 'Không thể tải báo cáo chi tiết giao dịch.');
      }
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [portalScope, query]);

  useEffect(() => {
    load();
    return () => { latestRequestRef.current += 1; };
  }, [load]);

  const changePage = (page) => {
    const params = new URLSearchParams(location.search);
    params.set('page', page);
    history.push({ pathname: location.pathname, search: params.toString() });
  };

  const exportExcel = async () => {
    if (!portalScope.store_id || exporting) return;
    const exportId = ++latestExportRef.current;
    const exportQuery = { ...query };
    const exportScope = scopeIdentity(portalScope);
    const exportLocation = { pathname: location.pathname, search: location.search };
    const isCurrent = () => exportId === latestExportRef.current
      && currentContextRef.current === contextKey
      && history.location.pathname === exportLocation.pathname
      && history.location.search === exportLocation.search
      && scopeIdentity(getPortalScope()) === exportScope;
    if (!isCurrent()) return;
    setExporting(true);
    setError('');
    try {
      const firstPage = await getSalesTransactionExportPage({ ...exportQuery, page: 1, items_per_page: 200 });
      if (!isCurrent()) return;
      const totalItems = Number(firstPage?.summary?.totalitems) || 0;
      const totalPages = Math.ceil(totalItems / 200);
      const allItems = [...(firstPage?.items || [])];
      for (let page = 2; page <= totalPages; page += 1) {
        if (!isCurrent()) return;
        const nextPage = await getSalesTransactionExportPage({ ...exportQuery, page, items_per_page: 200 });
        if (!isCurrent()) return;
        allItems.push(...(nextPage?.items || []));
      }
      if (!isCurrent()) return;
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(buildExportRows(allItems, Boolean(exportQuery.salesperson)));
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Giao dịch bán hàng');
      if (!isCurrent()) return;
      XLSX.writeFile(workbook, `giao-dich-ban-hang-${exportQuery.date_from}-${exportQuery.date_to}.xlsx`);
    } catch (requestError) {
      if (isCurrent()) setError(requestError?.message || 'Không thể xuất báo cáo Excel.');
    } finally {
      if (isCurrent()) setExporting(false);
    }
  };

  const toggleColumn = (column) => {
    if (column.required) return;
    setVisibleColumnKeys((current) =>
      current.includes(column.key)
        ? current.filter((key) => key !== column.key)
        : [...current, column.key],
    );
  };

  const availableColumns = TRANSACTION_COLUMNS.filter((column) => !column.requiresSalespersonFilter || query.salesperson);
  const visibleColumns = availableColumns.filter((column) => visibleColumnKeys.includes(column.key));
  const summary = data?.summary || {};
  const items = data?.items || [];
  const totalItems = Number(summary.totalitems) || 0;
  const pageSize = Number(summary.itemsperpage) || query.items_per_page;
  const currentPage = Number(summary.page) || query.page;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const contextItems = [
    { label: 'Cửa hàng', value: portalScope.store_name || 'Chưa chọn cửa hàng' },
    { label: 'Kỳ', value: `${formatDate(query.date_from)}–${formatDate(query.date_to)}` },
    { label: 'Theo', value: 'Ngày bán hàng' },
    ...(query.salesperson ? [{ label: 'Nhân viên bán hàng', value: query.salesperson }] : []),
  ];

  return (
    <ReportPageLayout reportId='transactions'>
      <ReportPageHeader
        title='Chi tiết giao dịch bán hàng'
        description='Đối chiếu từng dòng sản phẩm. Khi lọc nhân viên bán hàng, doanh số phân bổ là phần chia đều cho nhân viên đó; thành tiền vẫn là giá trị dòng chứng từ.'
        actions={(
          <button
            type='button'
            className='bc_rr_action_button is-secondary'
            onClick={load}
            disabled={loading || !portalScope.store_id}>
            <FiRefreshCw aria-hidden='true' /> Làm mới
          </button>
        )}
      />
      <ReportContextBar items={contextItems} />
      <SalesReportFilters includeDetailFilters loading={loading} />

      <ReportTableToolbar
        summary={(
          <>
            <strong>{number(summary.totalorders)} đơn</strong>
            <span>{number(totalItems)} dòng sản phẩm</span>
            <span>Tổng thành tiền: <b>{money(summary.totalamount)}</b></span>
            {query.salesperson ? <span>Doanh số phân bổ: <b>{money(summary.totalallocatedrevenue)}</b></span> : null}
          </>
        )}
        actions={(
          <>
            <button
              type='button'
              className='is-secondary'
              onClick={load}
              disabled={loading || !portalScope.store_id}>
              <FiRefreshCw aria-hidden='true' /> Làm mới
            </button>
            <details className='bc_rr_column_picker'>
              <summary>
                <FiColumns aria-hidden='true' /> Chọn cột
              </summary>
              <div className='bc_rr_column_picker_menu'>
                <strong>Hiển thị cột</strong>
                {availableColumns.map((column) => (
                  <label key={column.key}>
                    <input
                      type='checkbox'
                      checked={visibleColumnKeys.includes(column.key)}
                      disabled={column.required}
                      onChange={() => toggleColumn(column)}
                    />
                    <span>{column.label}</span>
                  </label>
                ))}
              </div>
            </details>
            {canExport ? (
              <button type='button' className='is-primary' onClick={exportExcel} disabled={exporting || !totalItems}>
                {exporting ? <FiRefreshCw className='is-spinning' aria-hidden='true' /> : <FiDownload aria-hidden='true' />}
                {exporting ? 'Đang xuất' : 'Xuất Excel'}
              </button>
            ) : null}
          </>
        )}
      />

      {!portalScope.store_id ? <ReportState>Vui lòng chọn cửa hàng trên thanh điều hướng.</ReportState> : null}
      {loading ? <ReportState type='loading'>Đang tải dữ liệu giao dịch...</ReportState> : null}
      {error ? <ReportState type='error'>{error}</ReportState> : null}

      {!loading && !error && portalScope.store_id ? (
        <section className='bc_rr_panel bc_rr_transaction_panel' aria-busy={loading}>
          <div className='bc_rr_table_wrap bc_rr_transaction_table' tabIndex='0' aria-label='Bảng giao dịch bán hàng'>
            <table className='bc_rr_table'>
              <thead>
                <tr>
                  {visibleColumns.map((column) => (
                    <th key={column.key} scope='col' className={column.className}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length ? items.map((item) => (
                  <tr key={item.orderdetailid}>
                    {visibleColumns.map((column) => (
                      <td key={column.key} className={column.className}>{column.render(item)}</td>
                    ))}
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={visibleColumns.length} className='bc_rr_empty'>
                      Không có giao dịch phù hợp bộ lọc.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className='bc_rr_pagination' aria-label='Phân trang'>
            <span>Trang {currentPage}/{totalPages}</span>
            <div>
              <button type='button' disabled={currentPage <= 1} onClick={() => changePage(currentPage - 1)}>Trước</button>
              <button type='button' disabled={currentPage >= totalPages} onClick={() => changePage(currentPage + 1)}>Sau</button>
            </div>
          </div>
        </section>
      ) : null}
    </ReportPageLayout>
  );
};

export default SalesTransactionReportPage;
